import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminApi } from '@/lib/adminApi';
import { feedbackApi } from '@/lib/feedbackApi';
import { filterTasks, taskActionLabel, taskList, type Task } from '@/lib/tasks';
import { AdminPage, RegistryTable, StatusPill, FeedbackControl } from '@/admin';

const REGISTRY = 'tasks';

export function TasksPage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['tasks'], queryFn: () => adminApi.tasks.list() });
  const pq = useQuery({ queryKey: ['panels'], queryFn: () => adminApi.panels.list() });
  const fb = useQuery({ queryKey: ['feedback-summary', REGISTRY], queryFn: () => feedbackApi.summary(REGISTRY) });
  const [search, setSearch] = useState('');

  const all = taskList(q.data);
  const rows = filterTasks(all, search);
  const panels = pq.data?.panels ?? {};
  const refresh = () => void qc.invalidateQueries({ queryKey: ['feedback-summary', REGISTRY] });

  return (
    <AdminPage title="Task Registry" description="User-facing prescriptions — each task fills one or more panels. Many-to-many with panels; a mode promotes tasks to required.">
      {q.data ? <div className="mb-13 text-body-sm text-text-secondary">{all.length} tasks · registry v{String(q.data.version ?? '?')}</div> : null}
      <RegistryTable<Task>
        columns={[
          { key: 'task', header: 'Task', render: (t) => (
            <div>
              <div className="font-semibold">{t.label}</div>
              <div className="font-mono text-[11px] text-text-muted">{t.task_id}</div>
            </div>
          ) },
          { key: 'category', header: 'Category', className: 'text-[12px] text-text-secondary', render: (t) => t.category || '—' },
          { key: 'universal', header: '', render: (t) => t.universal ? <StatusPill on label="universal" /> : null },
          { key: 'action', header: 'Action', className: 'font-mono text-[11px] text-text-secondary', render: (t) => taskActionLabel(t) },
          { key: 'panels', header: 'Satisfies panels', render: (t) => (
            <div className="flex flex-wrap gap-1">
              {(t.satisfies_panels ?? []).map((pid) => (
                <Link key={pid} to={`/admin/panels/${encodeURIComponent(pid)}`} className="rounded-pill border border-border px-2 py-0.5 text-[11px] text-accent hover:bg-surface-sunken">{panels[pid]?.label ?? pid}</Link>
              ))}
              {(t.satisfies_panels ?? []).length === 0 ? <span className="text-text-muted text-[12px]">—</span> : null}
            </div>
          ) },
          { key: 'used', header: 'Used by', className: 'font-mono text-[12px]', render: (t) => (t.used_by ?? []).length || '—' },
        ]}
        rows={rows}
        getRowKey={(t) => t.task_id}
        count={all.length}
        state={{ loading: q.isLoading, error: q.isError }}
        emptyText={search ? `No tasks match "${search}"` : 'No tasks.'}
        search={{ value: search, onChange: setSearch, placeholder: 'Search by label, id, category, or panel…' }}
        rowActions={(t) => <FeedbackControl registry={REGISTRY} itemId={t.task_id} itemLabel={t.label} openCount={fb.data?.[t.task_id]?.open ?? 0} onChanged={refresh} />}
      />
    </AdminPage>
  );
}
